import { useEffect, useState } from 'react';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/useAuth';
import { portalApi } from '../api';
import type { PublicContentDetail } from '../types';

interface ContentDetailOverlayProps {
  contentId: string;
  onClose: () => void;
}

const KIND_ICON: Record<string, string> = {
  NEWS: '📰', QUICK_POLL: '📊', ACHIEVEMENT: '🏆', EVENT: '📅',
  COMPETITION: '🎖️', MATERIAL: '📚', EDUCATION: '🎓', VACANCY: '💼',
};
const KIND_COLOR: Record<string, string> = {
  NEWS: '#41d2ff', QUICK_POLL: '#a78bfa', ACHIEVEMENT: '#fbbf24', EVENT: '#34d399',
  COMPETITION: '#f97316', MATERIAL: '#38bdf8', EDUCATION: '#4ade80', VACANCY: '#fb7185',
};

function fmtDate(iso?: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' });
}

export const ContentDetailOverlay = ({ contentId, onClose }: ContentDetailOverlayProps) => {
  const { t } = useI18n();
  const { status } = useAuth();
  const isAuthenticated = status === 'authenticated';
  const [item, setItem] = useState<PublicContentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [voteIndices, setVoteIndices] = useState<number[]>([]);
  const [voteLoading, setVoteLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState<string[]>([]);
  const [subLoading, setSubLoading] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    setLoading(true);
    portalApi.detail(contentId).then(data => {
      setItem(data);
      setSaved(data.user_has_saved);
      setVoteIndices(data.user_vote_indices);
    }).finally(() => setLoading(false));
    return () => { document.body.style.overflow = ''; };
  }, [contentId]);

  useEffect(() => {
    if (!isAuthenticated) {
      setSubscriptions([]);
      return;
    }
    portalApi.listHashtagSubscriptions()
      .then(subs => setSubscriptions(subs.map(s => s.tag)))
      .catch(() => setSubscriptions([]));
  }, [isAuthenticated, contentId]);

  const handleSave = async () => {
    if (!isAuthenticated || !item) return;
    try {
      const res = await portalApi.toggleFavorite(item.id);
      setSaved(res.is_saved);
    } catch {}
  };

  const handleSubscribeTag = async (tag: string) => {
    if (!isAuthenticated || subLoading) return;
    setSubLoading(true);
    try {
      const norm = tag.trim().toLowerCase().replace(/\s+/g, '_').replace(/^#/, '');
      const subscribed = subscriptions.includes(norm);
      if (subscribed) {
        await portalApi.unsubscribeHashtag(norm);
        setSubscriptions(prev => prev.filter(t => t !== norm));
      } else {
        await portalApi.subscribeHashtag(tag);
        setSubscriptions(prev => [...prev, norm]);
      }
    } catch {}
    setSubLoading(false);
  };

  const handleVote = async (idx: number) => {
    if (!isAuthenticated || !item || voteLoading) return;
    setVoteLoading(true);
    try {
      const indices = item.poll_allow_multiple
        ? voteIndices.includes(idx) ? voteIndices.filter(i => i !== idx) : [...voteIndices, idx]
        : [idx];
      await portalApi.votePoll(item.id, indices);
      setVoteIndices(indices);
    } catch {}
    setVoteLoading(false);
  };

  const color = item ? KIND_COLOR[item.kind] || '#41d2ff' : '#41d2ff';

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(4,10,20,0.97)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 16px', overflowY: 'auto' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ width: '100%', maxWidth: 860, background: '#0c1322', border: '1px solid #1e293b', borderRadius: 16, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #1e293b', background: '#090f1b' }}>
          <button onClick={onClose} style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-soft)', background: '#1e293b', border: 'none', padding: '7px 14px', borderRadius: 8, cursor: 'pointer' }}>
            {t('portal.detail.back' as any)}
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {isAuthenticated && item && (
              <button
                onClick={handleSave}
                style={{ fontSize: 12, fontWeight: 700, padding: '7px 14px', borderRadius: 8, border: `1px solid ${saved ? '#41d2ff44' : '#1e293b'}`, background: saved ? '#41d2ff18' : '#1e293b', color: saved ? '#41d2ff' : 'var(--text-soft)', cursor: 'pointer' }}
              >
                {saved ? t('portal.detail.saved' as any) : t('portal.detail.save' as any)}
              </button>
            )}
            <button onClick={onClose} style={{ fontSize: 12, fontWeight: 700, padding: '7px 14px', borderRadius: 8, border: 'none', background: '#1e293b', color: 'var(--text-soft)', cursor: 'pointer' }}>
              ✕
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-dim)' }}>Загрузка...</div>
        ) : !item ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-dim)' }}>Контент не найден</div>
        ) : (
          <div style={{ padding: '24px 28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <span style={{ fontSize: 22 }}>{KIND_ICON[item.kind] || '📄'}</span>
              <span style={{ fontSize: 11, fontWeight: 900, color, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                {t(`publisher.kind.${item.kind}` as any) || item.kind}
              </span>
              {item.is_promoted && <span style={{ fontSize: 10, fontWeight: 900, color: '#fbbf24', background: '#fbbf2418', padding: '3px 8px', borderRadius: 20, marginLeft: 'auto' }}>★ ТОП</span>}
            </div>

            <h2 style={{ margin: '0 0 12px', fontSize: 22, lineHeight: 1.35 }}>{item.title}</h2>

            {item.author_name && (
              <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 14 }}>
                {item.author_name}{item.organization && ` · ${item.organization}`}
                {item.published_at && ` · ${fmtDate(item.published_at)}`}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
              {item.meta?.level && <span className="public-pill">{item.meta.level}</span>}
              {item.meta?.format_type && <span className="public-pill">{item.meta.format_type}</span>}
              {item.meta?.city && <span className="public-pill">{item.meta.city}</span>}
              {item.meta?.cost_type === 'FREE' && <span style={{ fontSize: 10, fontWeight: 900, color: '#4ade80', background: '#4ade8015', padding: '4px 8px', borderRadius: 20 }}>Бесплатно</span>}
            </div>

            <p style={{ margin: '0 0 18px', color: 'var(--text-soft)', fontSize: 15, lineHeight: 1.65 }}>{item.summary}</p>

            {item.body && (
              <div style={{ margin: '0 0 20px', color: 'var(--text)', fontSize: 14, lineHeight: 1.75, borderTop: '1px solid #1e293b', paddingTop: 18, whiteSpace: 'pre-wrap' }}>
                {item.body}
              </div>
            )}

            {item.kind === 'QUICK_POLL' && item.poll_options.length > 0 && (
              <div style={{ margin: '0 0 20px', padding: 16, background: '#101a2e', borderRadius: 12, border: '1px solid #1e293b' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-dim)', marginBottom: 12 }}>
                  {item.poll_closed_at && new Date(item.poll_closed_at) < new Date()
                    ? t('portal.detail.pollClosed' as any)
                    : t('portal.detail.votePoll' as any)}
                </div>
                {item.poll_options.map(opt => (
                  <button
                    key={opt.index}
                    disabled={voteLoading || !isAuthenticated}
                    onClick={() => handleVote(opt.index)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '9px 14px',
                      marginBottom: 8,
                      borderRadius: 9,
                      border: `1px solid ${voteIndices.includes(opt.index) ? color : '#1e293b'}`,
                      background: voteIndices.includes(opt.index) ? `${color}18` : '#0c1322',
                      color: 'var(--text)',
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    {opt.text}
                    {voteIndices.includes(opt.index) && ' ✓'}
                  </button>
                ))}
                <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 8 }}>
                  {t('portal.detail.participants' as any)}: {item.poll_total_votes}
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
              {item.deadline && (
                <div style={{ padding: '12px 14px', background: '#101a2e', borderRadius: 10, border: '1px solid #1e293b' }}>
                  <div style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, marginBottom: 4 }}>{t('portal.detail.deadline' as any)}</div>
                  <div style={{ fontSize: 13, color: new Date(item.deadline) < new Date() ? '#f87171' : '#fbbf24' }}>{fmtDate(item.deadline)}</div>
                </div>
              )}
              {item.event_start && (
                <div style={{ padding: '12px 14px', background: '#101a2e', borderRadius: 10, border: '1px solid #1e293b' }}>
                  <div style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, marginBottom: 4 }}>{t('portal.detail.eventDate' as any)}</div>
                  <div style={{ fontSize: 13 }}>{fmtDate(item.event_start)}{item.event_end && ` — ${fmtDate(item.event_end)}`}</div>
                </div>
              )}
              {item.meta?.direction && (
                <div style={{ padding: '12px 14px', background: '#101a2e', borderRadius: 10, border: '1px solid #1e293b' }}>
                  <div style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, marginBottom: 4 }}>{t('portal.detail.direction' as any)}</div>
                  <div style={{ fontSize: 13 }}>{item.meta.direction}</div>
                </div>
              )}
              {item.meta?.employment_type && (
                <div style={{ padding: '12px 14px', background: '#101a2e', borderRadius: 10, border: '1px solid #1e293b' }}>
                  <div style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, marginBottom: 4 }}>{t('portal.detail.employment' as any)}</div>
                  <div style={{ fontSize: 13 }}>{item.meta.employment_type}</div>
                </div>
              )}
              {item.meta?.salary_text && (
                <div style={{ padding: '12px 14px', background: '#101a2e', borderRadius: 10, border: '1px solid #1e293b' }}>
                  <div style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, marginBottom: 4 }}>Зарплата</div>
                  <div style={{ fontSize: 13 }}>{item.meta.salary_text}</div>
                </div>
              )}
            </div>

            {item.meta?.requirements_text && (
              <div style={{ marginBottom: 16, padding: '14px 16px', background: '#101a2e', borderRadius: 10, border: '1px solid #1e293b' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-dim)', marginBottom: 8 }}>{t('portal.detail.requirements' as any)}</div>
                <div style={{ fontSize: 13, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{item.meta.requirements_text}</div>
              </div>
            )}

            {item.meta?.conditions_text && (
              <div style={{ marginBottom: 16, padding: '14px 16px', background: '#101a2e', borderRadius: 10, border: '1px solid #1e293b' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-dim)', marginBottom: 8 }}>{t('portal.detail.conditions' as any)}</div>
                <div style={{ fontSize: 13, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{item.meta.conditions_text}</div>
              </div>
            )}

            {item.hashtags.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {item.hashtags.map(ht => {
                    const subscribed = subscriptions.includes(ht.tag);
                    return (
                      <button
                        key={ht.tag}
                        type="button"
                        disabled={!isAuthenticated || subLoading}
                        onClick={() => void handleSubscribeTag(ht.display_label)}
                        title={isAuthenticated ? (subscribed ? 'Отписаться' : 'Подписаться на хэштег') : 'Войдите для подписки'}
                        style={{
                          fontSize: 11,
                          color: subscribed ? '#49ddb7' : color,
                          background: subscribed ? '#49ddb715' : `${color}15`,
                          padding: '4px 9px',
                          borderRadius: 20,
                          border: 'none',
                          cursor: isAuthenticated ? 'pointer' : 'default',
                        }}
                      >
                        #{ht.display_label}{subscribed ? ' ✓' : ''}
                      </button>
                    );
                  })}
                </div>
                {isAuthenticated ? (
                  <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-dim)' }}>
                    Нажмите на хэштег, чтобы подписаться
                  </div>
                ) : null}
              </div>
            )}

            {item.official_source && (
              <div style={{ marginBottom: 16, padding: '12px 16px', background: '#101a2e', borderRadius: 10, border: `1px solid ${color}22`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-dim)', marginBottom: 4 }}>{t('portal.detail.official' as any)}</div>
                  <div style={{ fontSize: 12 }}>{item.official_source.label || item.official_source.domain}</div>
                </div>
                <a href={item.official_source.url} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 12, fontWeight: 700, color, background: `${color}15`, border: `1px solid ${color}33`, padding: '6px 12px', borderRadius: 8, textDecoration: 'none', whiteSpace: 'nowrap' }}
                  onClick={e => e.stopPropagation()}
                >
                  {t('portal.detail.openSource' as any)}
                </a>
              </div>
            )}

            {item.disclaimer_required && (
              <div style={{ padding: '10px 14px', background: '#1a1000', border: '1px solid #f97316aa', borderRadius: 10, fontSize: 11, color: '#f97316', marginBottom: 16 }}>
                ⚠ {t('portal.detail.disclaimer' as any)}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', paddingTop: 12, borderTop: '1px solid #1e293b' }}>
              <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>👁 {item.view_count} {t('portal.detail.views' as any)}</span>
              <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>🔖 {item.save_count}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
